import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, PendingTasks, inject, signal } from '@angular/core';
import { PhotoCrop, PhotoDescriptor, PhotosApiService } from '@core/api/services/photos-api.service';

export interface CirclePhotoDraft { photo: PhotoDescriptor; file: File; crop: PhotoCrop; }

@Component({
  selector: 'app-circle-photo-editor',
  standalone: true,
  template: `
    <div class="frame" [class.frame--active]="active" [attr.tabindex]="active ? 0 : -1" role="group" aria-label="Encuadre de la foto. Arrastrá la imagen o usá las flechas para acomodarla."
      [class.frame--dragging]="dragging()" [attr.aria-busy]="!ready()"
      (pointerdown)="start($event)" (pointermove)="move($event)" (pointerup)="end($event)" (pointercancel)="end($event)"
      (keydown)="key($event)" (keyup)="commit()" (blur)="commit()" (click)="$event.stopPropagation()">
      @if (ready()) {
        @if (!photo.framing && !dirty() && thumbnailUrl) {
          <img class="saved-thumbnail" [src]="thumbnailUrl" alt="Encuadre actual de la comunidad" draggable="false" />
        } @else {
          <img [src]="url()" alt="Foto de comunidad; arrastrá para ajustar el encuadre" draggable="false"
            [style.width.%]="width * 100 / crop().size" [style.height.%]="height * 100 / crop().size"
            [style.left.%]="-crop().x * 100 / crop().size" [style.top.%]="-crop().y * 100 / crop().size" />
        }
      } @else if (failed()) {
        <span role="alert">No se pudo cargar la foto.</span>
      } @else {
        <span class="loading" role="status" aria-label="Cargando foto"></span>
      }
    </div>
  `,
  styles: [`
    :host { position: absolute; inset: 0; display: block; pointer-events: none; }
    .frame { position: relative; width: 100%; height: 100%; border-radius: 50%; overflow: hidden; touch-action: none; cursor: grab; background: #edf3f9; }
    .frame--dragging { cursor: grabbing; }
    .frame--active { pointer-events: auto; }
    .frame:focus-visible { outline: 2px solid #1675e8; outline-offset: -3px; }
    img { position: absolute; display: block; max-width: none; user-select: none; pointer-events: none; }
    .saved-thumbnail { inset: 0; width: 100%; height: 100%; object-fit: cover; }
    .loading { position: absolute; inset: 0; border: 3px solid #dce7f1; border-top-color: #5c93c9; border-radius: 50%; animation: spin 700ms linear infinite; }
    span[role=alert] { display: grid; place-items: center; height: 100%; padding: 16px; box-sizing: border-box; font-size: 12px; }
    @keyframes spin { to { transform: rotate(360deg); } }
  `],
})
export class CirclePhotoEditorComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) photo!: PhotoDescriptor;
  @Input() disabled = false;
  @Input() active = true;
  @Input() thumbnailUrl?: string;
  @Output() framingChange = new EventEmitter<CirclePhotoDraft>();
  private readonly api = inject(PhotosApiService);
  private readonly tasks = inject(PendingTasks);
  readonly url = signal('');
  readonly ready = signal(false);
  readonly failed = signal(false);
  readonly dirty = signal(false);
  readonly dragging = signal(false);
  readonly crop = signal<PhotoCrop>({ x: 0, y: 0, size: 256 });
  width = 0;
  height = 0;
  private file?: File;
  private initial?: PhotoCrop;
  private retained?: string;
  private acceptedUrl?: string;
  private submitted?: PhotoCrop;
  private generation = 0;
  private pointer?: { id: number; x: number; y: number; crop: PhotoCrop; diameter: number };

  ngOnChanges() {
    if (this.retained === this.photo.mainUrl || this.acceptedUrl === this.photo.mainUrl) return;
    if (this.retained) this.api.release(this.retained);
    this.retained = this.photo.mainUrl;
    this.acceptedUrl = undefined; this.submitted = undefined;
    const generation = ++this.generation;
    this.ready.set(false); this.failed.set(false); this.dirty.set(false);
    void this.tasks.run(() => this.load(generation));
  }
  private async load(generation: number) {
    try {
      const url = await this.api.image(this.photo.mainUrl);
      const image = new Image(); image.src = url;
      await image.decode();
      const blob = await (await fetch(url)).blob();
      if (generation !== this.generation) return;
      this.width = image.naturalWidth; this.height = image.naturalHeight;
      this.file = new File([blob], 'photo.jpg', { type: 'image/jpeg' });
      const framing = this.photo.framing;
      const size = Math.min(this.width, this.height);
      this.initial = this.constrain(framing
        ? { x: framing.x * this.width, y: framing.y * this.height, size: framing.size * size }
        : { x: (this.width - size) / 2, y: (this.height - size) / 2, size });
      this.crop.set(this.initial); this.url.set(url); this.ready.set(true);
    } catch { if (generation === this.generation) this.failed.set(true); }
  }
  get zoom() { return this.ready() ? Math.min(this.width, this.height) / this.crop().size : 1; }
  get maxZoom() { return Math.max(1, Math.min(4, Math.min(this.width, this.height))); }
  private constrain(crop: PhotoCrop): PhotoCrop {
    const size = Math.min(Math.min(this.width, this.height), Math.max(1, Math.round(crop.size)));
    return { x: Math.max(0, Math.min(this.width - size, Math.round(crop.x))), y: Math.max(0, Math.min(this.height - size, Math.round(crop.y))), size };
  }
  private update(crop: PhotoCrop) {
    const next = this.constrain(crop);
    this.crop.set(next);
    this.dirty.set(!!this.initial && (next.x !== this.initial.x || next.y !== this.initial.y || next.size !== this.initial.size));
  }
  setZoom(value: number) {
    if (!this.ready() || this.disabled) return;
    const crop = this.crop(), size = Math.min(this.width, this.height) / Math.max(1, Math.min(this.maxZoom, value));
    this.update({ x: crop.x + (crop.size - size) / 2, y: crop.y + (crop.size - size) / 2, size });
  }
  reset() { if (this.initial) { this.crop.set(this.initial); this.dirty.set(false); } }
  draft(): CirclePhotoDraft | undefined {
    return this.file && this.dirty() ? { photo: this.photo, file: this.file, crop: this.crop() } : undefined;
  }
  commit() {
    if (this.disabled || !this.active || this.pointer) return;
    const draft = this.draft();
    if (!draft || (this.submitted && draft.crop.x === this.submitted.x && draft.crop.y === this.submitted.y && draft.crop.size === this.submitted.size)) return;
    this.submitted = { ...draft.crop };
    this.framingChange.emit(draft);
  }
  acceptSaved(photo: PhotoDescriptor) {
    if (photo.id !== this.photo.id) return;
    this.acceptedUrl = photo.mainUrl;
    this.initial = { ...this.crop() };
    this.dirty.set(false); this.submitted = undefined;
  }
  start(event: PointerEvent) {
    event.stopPropagation();
    if (!this.ready() || !this.active || this.disabled || event.button !== 0) return;
    const frame = event.currentTarget as HTMLElement;
    frame.setPointerCapture(event.pointerId);
    this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, crop: this.crop(), diameter: frame.getBoundingClientRect().width };
    this.dragging.set(true);
  }
  move(event: PointerEvent) {
    const pointer = this.pointer;
    if (!pointer || pointer.id !== event.pointerId || this.disabled) return;
    const scale = pointer.crop.size / pointer.diameter;
    this.update({ ...pointer.crop, x: pointer.crop.x - (event.clientX - pointer.x) * scale, y: pointer.crop.y - (event.clientY - pointer.y) * scale });
  }
  end(event: PointerEvent) {
    if (this.pointer?.id !== event.pointerId) return;
    this.pointer = undefined; this.dragging.set(false);
    const frame = event.currentTarget as HTMLElement;
    if (frame.hasPointerCapture(event.pointerId)) frame.releasePointerCapture(event.pointerId);
    this.commit();
  }
  key(event: KeyboardEvent) {
    event.stopPropagation();
    if (!this.ready() || this.disabled || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    const crop = this.crop(), delta = event.shiftKey ? 20 : 5;
    this.update({ ...crop, x: crop.x + (event.key === 'ArrowLeft' ? delta : event.key === 'ArrowRight' ? -delta : 0),
      y: crop.y + (event.key === 'ArrowUp' ? delta : event.key === 'ArrowDown' ? -delta : 0) });
  }
  ngOnDestroy() { ++this.generation; if (this.retained) this.api.release(this.retained); }
}
